import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  directoryApi,
  parcelsApi,
  walletApi,
  notificationsApi,
  vehiclesApi,
  usersApi,
  type ClientDirectoryEntry,
  type Parcel,
  type Wallet,
  type WalletTransaction,
  type Notification,
  type Vehicle,
} from "@/lib/api";
import { formatNaira } from "@/lib/mock-data";
import { timeAgo, cn } from "@/lib/utils";
import { ArrowLeft, User, Truck, Package, Wallet as WalletIcon, Activity as ActivityIcon, Mail } from "lucide-react";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";

const statusTone: Record<string, string> = {
  active: "bg-success/10 text-success border-success/20",
  inactive: "bg-muted text-muted-foreground border-border",
};

export default function ClientDashboard() {
  const { id } = useParams<{ id: string }>();
  const token = useAuth((s) => s.session?.accessToken);
  const [client, setClient] = useState<ClientDirectoryEntry | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [deliveries, setDeliveries] = useState<Parcel[]>([]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [activity, setActivity] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const c = await directoryApi.client(id, token);
        if (cancelled) return;
        setClient(c);

        const [allVehicles, deliveriesRes, w, txns, feed] = await Promise.all([
          vehiclesApi.list(),
          parcelsApi.list({ clientId: id }, token),
          walletApi.get(id, token).catch(() => null),
          walletApi.transactions(id, token).catch(() => []),
          notificationsApi.list(id, token).catch(() => []),
        ]);
        if (cancelled) return;
        setVehicles(allVehicles.filter((v) => v.owner_id === id));
        setDeliveries(deliveriesRes);
        setWallet(w);
        setTransactions(txns);
        setActivity(feed);
      } catch {
        if (!cancelled) toast.error("Failed to load client dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <>
        <Topbar title="Client dashboard" />
        <div className="flex-1 p-4 md:p-6"><LoadingBlock label="Loading dashboard…" /></div>
      </>
    );
  }

  if (!client) {
    return (
      <>
        <Topbar title="Client dashboard" />
        <div className="flex-1 p-8 text-center text-sm text-muted-foreground">Client not found.</div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={client.name}
        subtitle="Client dashboard"
        actions={
          <div className="flex items-center gap-2">
            {client.email && !client.activated ? (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await usersApi.resendActivation(client.id, token);
                    toast.success("Activation email resent");
                  } catch {
                    toast.error("Could not resend activation email");
                  }
                }}
              >
                <Mail className="mr-1.5 h-4 w-4" /> Resend activation
              </Button>
            ) : null}
            <Button variant="outline" size="sm" asChild>
              <Link to="/app/users/clients"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to clients</Link>
            </Button>
          </div>
        }
      />
      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="surface-card p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-soft text-primary">
              <User className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">{client.name}</h2>
                <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize", statusTone[client.status] ?? "bg-muted text-muted-foreground border-border")}>
                  {client.status}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">{client.email ?? "—"} · {client.phone ?? "—"}</div>
            </div>
          </div>
          <dl className="mt-4 grid min-w-0 grid-cols-2 gap-2 rounded-lg bg-muted/40 p-3 text-center sm:grid-cols-4">
            <div className="min-w-0"><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Vehicles</dt><dd className="mt-0.5 font-semibold">{vehicles.length}</dd></div>
            <div className="min-w-0"><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Shipments</dt><dd className="mt-0.5 font-semibold">{client.total_shipments}</dd></div>
            <div className="min-w-0"><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Total spent</dt><dd className="mt-0.5 truncate font-semibold">{formatNaira(client.total_spent_kobo / 100)}</dd></div>
            <div className="min-w-0"><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Wallet</dt><dd className="mt-0.5 truncate font-semibold">{wallet ? formatNaira(wallet.available_kobo / 100) : "—"}</dd></div>
          </dl>
        </div>

        <Tabs defaultValue="vehicles">
          <TabsList>
            <TabsTrigger value="vehicles"><Truck className="mr-1.5 h-3.5 w-3.5" /> Vehicles</TabsTrigger>
            <TabsTrigger value="deliveries"><Package className="mr-1.5 h-3.5 w-3.5" /> Deliveries</TabsTrigger>
            <TabsTrigger value="payments"><WalletIcon className="mr-1.5 h-3.5 w-3.5" /> Payments</TabsTrigger>
            <TabsTrigger value="activity"><ActivityIcon className="mr-1.5 h-3.5 w-3.5" /> Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="vehicles">
            <div className="surface-card">
              <Table>
                <TableHeader><TableRow><TableHead>Plate</TableHead><TableHead>Make/model</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {vehicles.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell className="font-mono text-xs">{v.plate}</TableCell>
                      <TableCell>{[v.make, v.model].filter(Boolean).join(" ") || "—"}</TableCell>
                      <TableCell className="capitalize">{v.status}</TableCell>
                    </TableRow>
                  ))}
                  {vehicles.length === 0 ? <TableRow><TableCell colSpan={3} className="py-6 text-center text-sm text-muted-foreground">No vehicles registered.</TableCell></TableRow> : null}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="deliveries">
            <div className="surface-card">
              <Table>
                <TableHeader><TableRow><TableHead>Tracking ID</TableHead><TableHead>Status</TableHead><TableHead>Amount</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
                <TableBody>
                  {deliveries.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-mono text-xs">{p.tracking_id}</TableCell>
                      <TableCell className="capitalize">{p.status.replace("_", " ")}</TableCell>
                      <TableCell>{formatNaira(p.amount_kobo / 100)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.created_at ? timeAgo(p.created_at) : "—"}</TableCell>
                    </TableRow>
                  ))}
                  {deliveries.length === 0 ? <TableRow><TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">No deliveries yet.</TableCell></TableRow> : null}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="payments">
            <div className="surface-card">
              <Table>
                <TableHeader><TableRow><TableHead>Reference</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                <TableBody>
                  {transactions.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="font-mono text-xs">{t.reference}</TableCell>
                      <TableCell className="capitalize">{t.type.replace("_", " ")}</TableCell>
                      <TableCell>{formatNaira(t.amount_kobo / 100)}</TableCell>
                      <TableCell className="capitalize">{t.status}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{timeAgo(t.occurred_at)}</TableCell>
                    </TableRow>
                  ))}
                  {transactions.length === 0 ? <TableRow><TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">No transactions yet.</TableCell></TableRow> : null}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <div className="surface-card divide-y">
              {activity.map((n) => (
                <div key={n.id} className="p-3">
                  <div className="text-sm font-medium">{n.title}</div>
                  {n.detail ? <div className="text-xs text-muted-foreground">{n.detail}</div> : null}
                  <div className="mt-1 text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</div>
                </div>
              ))}
              {activity.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No activity yet.</div> : null}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
